import { motion } from "framer-motion";
import { ChevronRight, Trophy, Users2 } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { cn } from "@/utils/cn";
import type { LeagueSummary } from "@/modules/leagues/types/league";
import { getCompetitionHero } from "@/config/competitionHeroes";

interface LeagueCardProps {
  league: LeagueSummary;
  onClick: () => void;
}

const STATUS_LABEL: Record<LeagueSummary["status"], string> = {
  aberta: "Aberta",
  em_andamento: "Em andamento",
  encerrada: "Encerrada",
};

/**
 * Card de Liga — usado na Home e em "Minhas Ligas". Reúne todos os campos
 * pedidos: banner, nome da liga, nome da competição, participantes, status,
 * próxima rodada, indicador Ao Vivo, partidas pendentes, código da liga.
 */
export function LeagueCard({ league, onClick }: LeagueCardProps) {
  const heroImage = getCompetitionHero(league.competitionId);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className="group flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-left shadow-soft transition-shadow hover:shadow-elevated"
    >
      {/* Banner do campeonato — usa a Hero real da competição quando
          cadastrada (item 5-10 do sistema de Hero por competição); sem
          imagem própria ainda, cai no gradiente + bola decorativa como
          HERO_DEFAULT temporário (item 11), nunca um fundo quebrado/vazio. */}
      <div
        className="relative flex h-20 items-end justify-between overflow-hidden bg-gradient-hero bg-cover bg-center px-4 py-3"
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
      >
        {heroImage ? (
          // Overlay escuro só embaixo, pra manter o nome da competição
          // sempre legível sem esconder o resto da arte (item 7 e 8).
          <div
            className="absolute inset-0"
            aria-hidden="true"
            style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 80%)" }}
          />
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-hero-radial" aria-hidden="true" />
            {/* Bola desbotada, só decorativa — só aparece no fallback sem
                Hero própria (item 23: a imagem real não leva decoração extra) */}
            <svg
              className="pointer-events-none absolute -right-3 -top-3 h-24 w-24 text-white/10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m12 7 3.5 2.5-1.3 4.1H9.8L8.5 9.5 12 7Z" fill="currentColor" stroke="none" />
              <path d="M12 2v2M21 12h-2M12 22v-2M3 12h2M18.5 5.5l-1.4 1.4M18.5 18.5l-1.4-1.4M5.5 18.5l1.4-1.4M5.5 5.5l1.4 1.4" />
            </svg>
          </>
        )}
        <span className="relative font-display text-[11px] font-bold uppercase tracking-wide text-white/85 [text-shadow:0_1px_3px_rgb(0_0_0_/_0.5)]">
          {league.competitionName} · {league.season}
        </span>
        {league.hasLiveMatch && (
          <span className="relative inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur-sm">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-400 text-emerald-400" /> Ao vivo
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-display text-base font-bold leading-tight">{league.nome}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Código: {league.inviteCode}</p>
          </div>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="primary">
            <Users2 className="h-3 w-3" /> {league.participantsCount} participantes
          </Badge>
          <Badge variant="outline">{league.currentRoundLabel}</Badge>
          <Badge variant={league.status === "encerrada" ? "default" : "success"}>
            {STATUS_LABEL[league.status]}
          </Badge>
        </div>

        <div
          className={cn(
            "flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold",
            league.pendingPredictionsCount > 0
              ? "bg-accent-50 text-accent-700 dark:bg-accent-500/20 dark:text-accent-300"
              : "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200"
          )}
        >
          <Trophy className="h-3.5 w-3.5" />
          {league.pendingPredictionsCount > 0
            ? `${league.pendingPredictionsCount} partida${league.pendingPredictionsCount > 1 ? "s" : ""} pendente${league.pendingPredictionsCount > 1 ? "s" : ""} para palpitar`
            : "Todos os palpites da rodada em dia"}
        </div>
      </div>
    </motion.button>
  );
}
